import { relations } from 'drizzle-orm'
import {
  boolean,
  index,
  integer,
  pgEnum,
  pgTable,
  real,
  text,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getMonetaryField,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { customers } from './customer'
import { AiQualityScores } from './inboxItem'
import { opportunities } from './opportunity'
import { users } from './user'

// ────────────────────────────────────────────────────────────
// Enums
// ────────────────────────────────────────────────────────────

// 'opened' is intentionally NOT a status. An offer that has been opened
// is still in the 'sent' state until the customer accepts/rejects; the
// "has it been opened" signal is derived from the event log instead.
// Otherwise an opened-then-accepted offer's status would lose the
// open info, and the state machine would have an ambiguous transition.
export const offerStatusEnum = pgEnum('offer_status', [
  'draft',
  'sent',
  'accepted',
  'expired',
  'rejected',
  'cancelled',
])

// Append-only lifecycle log. Each transition is one row; readers derive
// "is this offer accepted?" / "when was it sent?" by querying events
// instead of denormalized timestamp columns. Adding a new auditable
// transition costs one enum value, not a column.
export const offerEventTypeEnum = pgEnum('offer_event_type', [
  'created',
  'edited',
  'regenerated',
  'sent',
  'opened',
  'accepted',
  'rejected',
  'expired',
  'cancelled',
])

export const offerLineItemSourceEnum = pgEnum('offer_line_item_source', [
  'catalog',
  'custom',
])

export const offerLineItemCatalogTypeEnum = pgEnum(
  'offer_line_item_catalog_type',
  ['maintenance_task', 'product'],
)

// Drives which block templates are loaded for the offer (Trust, References,
// Klarna, etc.). AI sets it from opportunity context; seller can override
// via a Select in the editor, which re-resolves the optional blocks.
//
// Stored as text (not enum) so adding a new category later is a one-line
// change in the Zod schema + Select options — no migration needed. The
// API still validates against the Zod enum in @markku/types so callers
// can't insert arbitrary strings.

// ────────────────────────────────────────────────────────────
// JSONB content types (presentation-only, not queried)
// ────────────────────────────────────────────────────────────

// Local — kept as an interface for the typedJsonb<…>() annotation only.
// Public consumers should use OfferWorkContentItem from @markku/types
// (z.infer of offerOfferWorkContentItemSchema, same shape).
interface OfferWorkContentItem {
  id: string
  catalogRef?: { type: 'maintenance_task' | 'product'; id: number }
  title: string
  items: string[]
}

export interface OfferTrustItem {
  id: string
  title: string
  description: string
}

export interface OfferReferenceItem {
  id: string
  title: string
  location: string
  description: string
}

export type OfferOptionalBlock =
  | { id: string; type: 'trust'; title: string; items: OfferTrustItem[] }
  | {
      id: string
      type: 'references'
      title: string
      items: OfferReferenceItem[]
    }
  | { id: string; type: 'klarna'; title: string; bodyText: string }
  | {
      id: string
      type: 'kotitalousvahennys'
      title: string
      bodyText: string
      // True = informational mode: hide labor/deduction breakdown,
      // keep only title + bodyText.
      hideBreakdown?: boolean
    }
  | { id: string; type: 'custom'; title: string; bodyText: string }

// Snapshot of an AI-generated offer suggestion, stored on the offer row so
// we can later compare what the seller saved against what the AI proposed.
// Mirrors the public OfferSuggestion shape from the offer module — we only
// need the fields the seller can edit, not derived/server-resolved data.
export interface OfferAiDraftSnapshot {
  serviceDescription: string
  summary: string
  lahtotilanne: string | null
  edellytykset: string[]
  eiSisally: string[]
  workContent: Array<{ title: string; items: string[] }>
  optionalBlocks: OfferOptionalBlock[]
  priceBoxes: Array<{
    title: string
    extraText: string | null
    highlight: boolean
    lineItems: Array<{
      catalogType: 'maintenance_task' | 'product' | null
      catalogId: number | null
      title: string
      quantity: number
      unit: string
      unitPrice: number
      isLabor: boolean
    }>
  }>
}

// ────────────────────────────────────────────────────────────
// Block templates — admin-managed defaults per task category
// ────────────────────────────────────────────────────────────

export const offerBlockTypeEnum = pgEnum('offer_block_type', [
  'trust',
  'references',
  'klarna',
  'kotitalousvahennys',
  'custom',
])

export const offerBlockTemplates = pgTable(
  'offer_block_templates',
  {
    ...getBaseFields(),
    type: offerBlockTypeEnum().notNull(),
    // Matches maintenance_tasks.category. '_default' = applies to all categories.
    taskCategory: text('task_category').notNull().default('_default'),
    title: text().notNull(),
    // JSONB payload — shape depends on type. Stored as OfferOptionalBlock minus id.
    content: typedJsonb<OfferOptionalBlock>('content').notNull(),
    sortOrder: integer('sort_order').notNull().default(0),
  },
  (table) => getBaseIndexes('offer_block_templates', table),
)

// Audit trail for offer_block_templates edits. Each row captures the
// template state after an edit. Used by the admin UI to show managers
// when a shared template was changed and by whom.
export const offerBlockTemplateRevisions = pgTable(
  'offer_block_template_revisions',
  {
    ...getBaseFields(),
    templateId: getIdReference(() => offerBlockTemplates.id),
    editedByUserId: getIdReference(() => users.id),
    type: offerBlockTypeEnum().notNull(),
    taskCategory: text('task_category').notNull(),
    title: text().notNull(),
    content: typedJsonb<OfferOptionalBlock>('content').notNull(),
    sortOrder: integer('sort_order').notNull().default(0),
  },
  (table) => getBaseIndexes('offer_block_template_revisions', table),
)

export const offerBlockTemplateRevisionsRelations = relations(
  offerBlockTemplateRevisions,
  ({ one }) => ({
    template: one(offerBlockTemplates, {
      fields: [offerBlockTemplateRevisions.templateId],
      references: [offerBlockTemplates.id],
    }),
    editedBy: one(users, {
      fields: [offerBlockTemplateRevisions.editedByUserId],
      references: [users.id],
    }),
  }),
)

export const offerBlockTemplatesRelations = relations(
  offerBlockTemplates,
  ({ many }) => ({
    revisions: many(offerBlockTemplateRevisions),
  }),
)

// ────────────────────────────────────────────────────────────
// Tables
// ────────────────────────────────────────────────────────────

export const offers = pgTable(
  'offers',
  {
    ...getBaseFields(),
    // Origin
    opportunityId: getIdReference(() => opportunities.id),
    customerId: getNullableIdReference(() => customers.id),
    responsibleUserId: getNullableIdReference(() => users.id),

    // Identification
    number: text().notNull(), // T-2026-042
    status: offerStatusEnum().notNull().default('draft'),
    primaryCategory: text('primary_category'),

    // Contact + property details and customerType (pientalo / taloyhtio)
    // are derived from the linked opportunity at load time —
    // see synthesizeCustomer + the customerType derivation in
    // offerService.loadOffer. Sellers edit through the Liidin tiedot /
    // Kiinteistötiedot modals, which write straight to the opportunity.
    // The 'sent' event in offer_events.metadata captures a contact
    // snapshot for audit purposes.

    // Content
    serviceDescription: text().notNull().default(''),
    summary: text().notNull().default(''),
    lahtotilanne: text(),

    // Schedule + billing (editable per quote, seeded from defaults)
    aikataulutus: text().notNull().default(''),
    laskutus: text().notNull().default(''),

    // JSONB for presentation-only structures
    workContent: typedJsonb<OfferWorkContentItem[]>('work_content')
      .notNull()
      .default([]),
    optionalBlocks: typedJsonb<OfferOptionalBlock[]>('optional_blocks')
      .notNull()
      .default([]),
    edellytykset: typedJsonb<string[]>('edellytykset').notNull().default([]),
    eiSisally: typedJsonb<string[]>('ei_sisally').notNull().default([]),

    // Persisted send-email draft (subject + cover body). Populated on the
    // first call to draftEmailCover so re-opening the send modal returns
    // the same wording instead of a fresh Gemini call. Cleared on every
    // saveOffer so a content edit forces a regenerate next time. The
    // seller can also force a redraft via the modal's regenerate button.
    emailSubject: text('email_subject'),
    emailBody: text('email_body'),

    // Tracking & lifecycle
    // validForDays is the seller-controlled validity window in days.
    // validUntil is null until the offer is sent; markSent locks it to
    // sentAt + validForDays (end of day). Storing the days separately
    // (instead of an absolute date in the draft) avoids "Invalid Date"
    // edge cases and keeps the clock starting when the customer can act.
    validForDays: integer().notNull().default(14),
    validUntil: getTimestampWithTimezone(),
    webToken: text(),
    // Lifecycle transitions (sent / opened / accepted / rejected /
    // expired) live in the offer_events table now. Status remains as a
    // denormalized cache for fast filtering; event log is the truth.

    // AI quality tracking — populated when the seller used AI-luonti for
    // this offer. The "draft" is the last AI suggestion (overwritten on
    // each iteration). At save time we compare the saved offer against
    // the draft and flag whether it was accepted as-is or which fields
    // the seller edited.
    aiDraftSuggestion: typedJsonb<OfferAiDraftSnapshot>('ai_draft_suggestion'),
    aiDraftSuggestedAt: getTimestampWithTimezone(),
    aiDraftAcceptedAsIs: boolean('ai_draft_accepted_as_is'),
    aiDraftEditedFields: typedJsonb<string[]>('ai_draft_edited_fields'),
    aiDraftEvaluatedAt: getTimestampWithTimezone(),
    // Reserved for v2 (Gemini-graded quality). Kept null in v1 — we only
    // do a structural diff for now.
    aiQualityScore: real(),
    aiQualityScores: typedJsonb<AiQualityScores>('ai_quality_scores'),
    aiQualityReason: text(),
  },
  (table) => [
    ...getBaseIndexes('offers', table),
    index('offers_opportunity_id_idx').on(table.opportunityId),
  ],
)

export const offerPriceBoxes = pgTable(
  'offer_price_boxes',
  {
    ...getBaseFields(),
    offerId: getIdReference(() => offers.id),
    sortOrder: integer().notNull().default(0),
    title: text().notNull(),
    extraText: text(),
    highlight: boolean().notNull().default(false),
  },
  (table) => getBaseIndexes('offer_price_boxes', table),
)

export const offerLineItems = pgTable(
  'offer_line_items',
  {
    ...getBaseFields(),
    priceBoxId: getIdReference(() => offerPriceBoxes.id),
    sortOrder: integer().notNull().default(0),

    // Catalog source (soft reference — we snapshot, deletion of catalog item must NOT cascade)
    source: offerLineItemSourceEnum().notNull().default('custom'),
    catalogType: offerLineItemCatalogTypeEnum(),
    catalogId: text(), // soft id reference, kept as text to avoid FK constraint

    // Snapshot fields
    title: text().notNull(),
    quantity: getMonetaryField().notNull().default('1'),
    unit: text().notNull().default('kpl'),
    unitPrice: getMonetaryField().notNull().default('0'),
    isLabor: boolean().notNull().default(true),

    // AI review tracking: aiGenerated rows start with aiReviewedAt=null and
    // the editor shows a "AI-ehdotus, tarkista" badge until the seller
    // either accepts as-is (click) or edits any field (auto-confirm).
    aiGenerated: boolean().notNull().default(false),
    aiReviewedAt: getTimestampWithTimezone(),
  },
  (table) => getBaseIndexes('offer_line_items', table),
)

// Per-offer event metadata — minimal shared shape; event-type-specific
// fields are loose so we can add new transitions without schema changes.
export type OfferEventMetadata = {
  acceptedBy?: string
  acceptedNotes?: string
  rejectedReason?: string
  cancelledBy?: string
  cancelledReason?: string
  source?: string
  shortUrlKey?: string
  userPrompt?: string
  aiModel?: string
  changedFields?: string[]
  // Captured from the public offer page request — used by the ERP
  // tracking timeline to render device/browser per event.
  userAgent?: string
  // Snapshot of the recipient at send time. Contact + property data live
  // on the opportunity (not the offer), so without this snapshot we'd
  // lose the audit trail "what address / email did this offer actually
  // go to" the moment a seller fixes a typo on the lead. Captured by
  // sendOfferEmail when status flips to 'sent'.
  recipientSnapshot?: {
    name: string
    address: string
    contactPerson: string | null
    phone: string | null
    email: string | null
  }
}

export const offerEvents = pgTable(
  'offer_events',
  {
    ...getBaseFields(),
    offerId: getIdReference(() => offers.id),
    type: offerEventTypeEnum().notNull(),
    userId: getNullableIdReference(() => users.id),
    metadata: typedJsonb<OfferEventMetadata>('metadata').notNull().default({}),
  },
  (table) => [
    ...getBaseIndexes('offer_events', table),
    index('offer_events_offer_id_idx').on(table.offerId),
    index('offer_events_type_idx').on(table.type),
  ],
)

// ────────────────────────────────────────────────────────────
// Relations
// ────────────────────────────────────────────────────────────

export const offersRelations = relations(offers, ({ one, many }) => ({
  opportunity: one(opportunities, {
    fields: [offers.opportunityId],
    references: [opportunities.id],
  }),
  customer: one(customers, {
    fields: [offers.customerId],
    references: [customers.id],
  }),
  responsibleUser: one(users, {
    fields: [offers.responsibleUserId],
    references: [users.id],
  }),
  priceBoxes: many(offerPriceBoxes),
  events: many(offerEvents),
}))

export const offerEventsRelations = relations(offerEvents, ({ one }) => ({
  offer: one(offers, {
    fields: [offerEvents.offerId],
    references: [offers.id],
  }),
  user: one(users, {
    fields: [offerEvents.userId],
    references: [users.id],
  }),
}))

export const offerPriceBoxesRelations = relations(
  offerPriceBoxes,
  ({ one, many }) => ({
    offer: one(offers, {
      fields: [offerPriceBoxes.offerId],
      references: [offers.id],
    }),
    lineItems: many(offerLineItems),
  }),
)

export const offerLineItemsRelations = relations(offerLineItems, ({ one }) => ({
  priceBox: one(offerPriceBoxes, {
    fields: [offerLineItems.priceBoxId],
    references: [offerPriceBoxes.id],
  }),
}))

// ────────────────────────────────────────────────────────────
// Offer settings — key-value table for admin-editable defaults
// (aikataulutus, laskutus boilerplate). Schema deliberately tiny so
// adding a new setting is one INSERT, not a column change.
// ────────────────────────────────────────────────────────────

export const offerSettings = pgTable(
  'offer_settings',
  {
    ...getBaseFields(),
    key: text().notNull().unique(),
    value: text().notNull(),
    updatedByUserId: getNullableIdReference(() => users.id),
  },
  (table) => getBaseIndexes('offer_settings', table),
)

export const offerSettingsRelations = relations(offerSettings, ({ one }) => ({
  updatedBy: one(users, {
    fields: [offerSettings.updatedByUserId],
    references: [users.id],
  }),
}))

// ────────────────────────────────────────────────────────────
// AI prompts — admin-editable system prompt with version history
// ────────────────────────────────────────────────────────────

// Editable system prompts for offer AI flows. One row per `kind`:
// - 'suggestion' — drives suggestFromOpportunity (full offer draft)
// - 'cover' — drives draftEmailCover (saateviesti)
// Both versioned via offer_ai_prompt_versions for audit + rollback.
export const offerAiPromptKindEnum = pgEnum('offer_ai_prompt_kind', [
  'suggestion',
  'cover',
])

export const offerAiPrompts = pgTable(
  'offer_ai_prompts',
  {
    ...getBaseFields(),
    kind: offerAiPromptKindEnum().notNull().default('suggestion'),
    systemInstructions: text().notNull(),
    updatedByUserId: getNullableIdReference(() => users.id),
  },
  (table) => getBaseIndexes('offer_ai_prompts', table),
)

export const offerAiPromptVersions = pgTable(
  'offer_ai_prompt_versions',
  {
    ...getBaseFields(),
    promptId: getIdReference(() => offerAiPrompts.id),
    systemInstructions: text().notNull(),
    createdByUserId: getNullableIdReference(() => users.id),
  },
  (table) => [
    ...getBaseIndexes('offer_ai_prompt_versions', table),
    index('offer_ai_prompt_versions_prompt_id_idx').on(table.promptId),
  ],
)

export const offerAiPromptsRelations = relations(
  offerAiPrompts,
  ({ one, many }) => ({
    updatedBy: one(users, {
      fields: [offerAiPrompts.updatedByUserId],
      references: [users.id],
    }),
    versions: many(offerAiPromptVersions),
  }),
)

export const offerAiPromptVersionsRelations = relations(
  offerAiPromptVersions,
  ({ one }) => ({
    prompt: one(offerAiPrompts, {
      fields: [offerAiPromptVersions.promptId],
      references: [offerAiPrompts.id],
    }),
    createdBy: one(users, {
      fields: [offerAiPromptVersions.createdByUserId],
      references: [users.id],
    }),
  }),
)
